// ══════════════════════════════════════════════════════════════════════════════
// ABRA IA - Super Assistant IA pour Cockpit Pro
// Layout 3 colonnes : Sidebar + Chat + Artifact Viewer (style Claude)
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send, Sparkles, FileText, Download, Share2, Copy, Check,
  Mic, StopCircle, Loader2, ChevronRight, X, RefreshCw,
  Coins, AlertTriangle, Zap, PanelRightClose, PanelRightOpen,
  Receipt, Calendar, FileCode, Presentation, Image, MessageCircle
} from 'lucide-react';
import { API_URL } from '../../config/api';

// ══════════════════════════════════════════════════════════════════════════════
// CONSTANTES & STYLES
// ══════════════════════════════════════════════════════════════════════════════

const COLORS = {
  primary: '#1E3A5F',
  primaryLight: '#2d5a8a',
  accent: '#E8650A',
  accentHover: '#d45a09',
  background: '#f8fafc',
  surface: '#ffffff',
  surfaceHover: '#f1f5f9',
  border: '#e2e8f0',
  textPrimary: '#1e293b',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
};

// Suggestions de prompts pour l'état vide
const PROMPT_SUGGESTIONS = [
  { icon: Receipt, label: 'Crée un devis', prompt: 'Crée un devis pour des travaux de rénovation de salle de bain' },
  { icon: FileText, label: 'Rédige un email', prompt: 'Rédige un email professionnel pour relancer un client' },
  { icon: Calendar, label: 'Planifie un chantier', prompt: 'Crée un planning pour un chantier de 2 semaines' },
  { icon: Presentation, label: 'Présentation', prompt: 'Crée une présentation de mon entreprise' },
];

// Types d'artifacts supportés
const ARTIFACT_TYPES = {
  devis: { icon: Receipt, color: '#10b981', label: 'Devis' },
  facture: { icon: Receipt, color: '#3b82f6', label: 'Facture' },
  document: { icon: FileText, color: '#8b5cf6', label: 'Document' },
  planning: { icon: Calendar, color: '#f59e0b', label: 'Planning' },
  code: { icon: FileCode, color: '#64748b', label: 'Code' },
  image: { icon: Image, color: '#ec4899', label: 'Image' },
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════

export default function AbraIAPage() {
  const context = useOutletContext();
  const { userProfile, token } = context || {};
  
  // États
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showArtifact, setShowArtifact] = useState(true);
  const [activeArtifact, setActiveArtifact] = useState(null);
  const [credits, setCredits] = useState({ balance: 0, loading: true });
  const [copiedId, setCopiedId] = useState(null);
  
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  
  // Charger le solde AbracaCoins
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const authToken = token || localStorage.getItem('client_token') || localStorage.getItem('ecosystem_token');
        if (!authToken) {
          setCredits({ balance: 500, loading: false }); // Démo
          return;
        }
        
        const res = await fetch(`${API_URL}/api/abracacoins/balance`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setCredits({ balance: data.total_available || 0, loading: false });
        } else {
          setCredits({ balance: 500, loading: false }); // Fallback démo
        }
      } catch (e) {
        console.error('Erreur chargement crédits:', e);
        setCredits({ balance: 500, loading: false });
      }
    };
    
    fetchCredits();
  }, [token]);
  
  // Auto-scroll vers le bas
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);
  
  // Envoyer un message
  const handleSendMessage = async (customPrompt = null) => {
    const messageText = customPrompt || inputValue.trim();
    if (!messageText || isLoading) return;
    
    // Ajouter le message utilisateur
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    
    try {
      const authToken = token || localStorage.getItem('client_token') || localStorage.getItem('ecosystem_token');
      
      const res = await fetch(`${API_URL}/api/ai/abra-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          message: messageText,
          context: 'cockpit_pro',
          user_id: userProfile?.id || 'demo_user'
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        
        // Construire le message assistant
        const assistantMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: data.response || data.message || "Voici ma réponse.",
          timestamp: new Date().toISOString(),
          artifact: data.artifact || null,
          credits_consumed: data.credits_consumed || 0
        };
        
        setMessages(prev => [...prev, assistantMessage]);
        
        // Mettre à jour les crédits
        if (data.credits_consumed) {
          setCredits(prev => ({ ...prev, balance: Math.max(0, prev.balance - data.credits_consumed) }));
        }
        
        // Ouvrir l'artifact si présent
        if (data.artifact) {
          setActiveArtifact(data.artifact);
          setShowArtifact(true);
        }
      } else {
        // Fallback : réponse simulée
        const fallbackResponse = generateFallbackResponse(messageText);
        setMessages(prev => [...prev, fallbackResponse]);
        
        if (fallbackResponse.artifact) {
          setActiveArtifact(fallbackResponse.artifact);
          setShowArtifact(true);
        }
      }
    } catch (error) {
      console.error('Erreur chat:', error);
      // Fallback en cas d'erreur
      const fallbackResponse = generateFallbackResponse(messageText);
      setMessages(prev => [...prev, fallbackResponse]);
      
      if (fallbackResponse.artifact) {
        setActiveArtifact(fallbackResponse.artifact);
        setShowArtifact(true);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Générer une réponse de fallback (mode démo)
  const generateFallbackResponse = (userMsg) => {
    const msg = userMsg.toLowerCase();
    let artifact = null;
    let content = '';
    
    if (msg.includes('devis') || msg.includes('estimation')) {
      content = "J'ai préparé un devis pour toi. Tu peux le consulter dans le panneau de droite, le modifier et l'envoyer directement au client.";
      artifact = {
        type: 'devis',
        title: 'Devis Travaux',
        subtitle: 'Rénovation salle de bain',
        preview: 'Total: 5 976€ TTC',
        content: generateDevisContent()
      };
    } else if (msg.includes('facture')) {
      content = "Voici la facture générée. Tu peux la télécharger en PDF et l'envoyer au client.";
      artifact = {
        type: 'facture',
        title: 'Facture',
        subtitle: `F-2024-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
        preview: 'Montant: 5 976€ TTC',
        content: generateFactureContent()
      };
    } else if (msg.includes('email') || msg.includes('mail') || msg.includes('message')) {
      content = "Voici le message rédigé. Tu peux le copier et l'envoyer ou le modifier selon tes besoins.";
      artifact = {
        type: 'document',
        title: 'Email Professionnel',
        subtitle: 'Relance client',
        preview: 'Objet: Votre projet de rénovation',
        content: generateEmailContent()
      };
    } else if (msg.includes('planning') || msg.includes('calendrier') || msg.includes('chantier')) {
      content = "J'ai créé un planning pour ton chantier. Tu peux l'ajuster selon tes disponibilités.";
      artifact = {
        type: 'planning',
        title: 'Planning Chantier',
        subtitle: 'Semaine 17-18',
        preview: '5 phases planifiées',
        content: generatePlanningContent()
      };
    } else {
      content = "Je suis là pour t'aider ! Je peux créer des devis, rédiger des emails, planifier tes chantiers, ou générer tout type de document professionnel. Dis-moi ce dont tu as besoin.";
    }
    
    return {
      id: Date.now() + 1,
      role: 'assistant',
      content,
      timestamp: new Date().toISOString(),
      artifact,
      credits_consumed: artifact ? 5 : 1
    };
  };
  
  // Copier le contenu
  const handleCopy = async (content, id) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (e) {
      console.error('Erreur copie:', e);
    }
  };
  
  // Télécharger l'artifact
  const handleDownload = (artifact) => {
    const blob = new Blob([artifact.content || ''], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${artifact.title || 'document'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  // Raccourci clavier Enter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div 
      style={{
        display: 'flex',
        height: 'calc(100vh - 120px)',
        background: COLORS.background,
        borderRadius: '16px',
        overflow: 'hidden',
        border: `1px solid ${COLORS.border}`,
      }}
    >
      {/* ═══════════════════════════════════════════════════════════════════════
          COLONNE CHAT (Centre)
          ═══════════════════════════════════════════════════════════════════════ */}
      <div 
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: COLORS.surface,
          borderRight: showArtifact && activeArtifact ? `1px solid ${COLORS.border}` : 'none',
        }}
      >
        {/* Header Chat */}
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: `1px solid ${COLORS.border}`,
            background: COLORS.surface,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div 
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryLight} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Sparkles size={20} color="#ffffff" />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: COLORS.textPrimary }}>
                Super Assistant IA
              </h1>
              <p style={{ margin: 0, fontSize: '13px', color: COLORS.textSecondary }}>
                Abra est là pour t'aider
              </p>
            </div>
          </div>
          
          {/* Badge AbracaCoins */}
          <div 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 14px',
              borderRadius: '12px',
              background: credits.balance < 100 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(232, 101, 10, 0.1)',
              border: `1px solid ${credits.balance < 100 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(232, 101, 10, 0.2)'}`,
            }}
          >
            {credits.balance < 100 ? (
              <AlertTriangle size={16} color={COLORS.error} />
            ) : (
              <Coins size={16} color={COLORS.accent} />
            )}
            <span 
              style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: credits.balance < 100 ? COLORS.error : COLORS.accent 
              }}
            >
              {credits.loading ? '...' : credits.balance.toLocaleString()}
            </span>
            <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>AbracaCoins</span>
          </div>
        </div>
        
        {/* Zone de Chat */}
        <div 
          ref={chatContainerRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px',
          }}
        >
          {messages.length === 0 ? (
            // État vide - Suggestions
            <div style={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '32px',
            }}>
              <div style={{ textAlign: 'center' }}>
                <div 
                  style={{
                    width: '80px',
                    height: '80px',
                    margin: '0 auto 16px',
                    borderRadius: '20px',
                    background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryLight} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Sparkles size={36} color="#ffffff" />
                </div>
                <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: COLORS.textPrimary }}>
                  Bonjour{userProfile?.name ? `, ${userProfile.name.split(' ')[0]}` : ''} !
                </h2>
                <p style={{ margin: '8px 0 0', fontSize: '15px', color: COLORS.textSecondary, maxWidth: '400px' }}>
                  Je suis Abra, ton assistant IA. Je peux créer des devis, rédiger des emails, planifier tes chantiers et bien plus encore.
                </p>
              </div>
              
              {/* Grille de suggestions */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '12px',
                width: '100%',
                maxWidth: '480px',
              }}>
                {PROMPT_SUGGESTIONS.map((suggestion, idx) => {
                  const Icon = suggestion.icon;
                  return (
                    <button
                      key={idx}
                      onClick={() => handleSendMessage(suggestion.prompt)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '14px 16px',
                        background: COLORS.surface,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = COLORS.accent;
                        e.currentTarget.style.background = 'rgba(232, 101, 10, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = COLORS.border;
                        e.currentTarget.style.background = COLORS.surface;
                      }}
                    >
                      <Icon size={20} color={COLORS.accent} />
                      <span style={{ fontSize: '14px', fontWeight: '500', color: COLORS.textPrimary }}>
                        {suggestion.label}
                      </span>
                      <ChevronRight size={16} color={COLORS.textMuted} style={{ marginLeft: 'auto' }} />
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            // Messages
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {messages.map((msg) => (
                <div 
                  key={msg.id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  {/* Bulle de message */}
                  <div 
                    style={{
                      maxWidth: '80%',
                      padding: '14px 18px',
                      borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                      background: msg.role === 'user' ? COLORS.primary : COLORS.surfaceHover,
                      color: msg.role === 'user' ? '#ffffff' : COLORS.textPrimary,
                    }}
                  >
                    <p style={{ margin: 0, fontSize: '15px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </p>
                  </div>
                  
                  {/* Artifact Card (pour les messages assistant) */}
                  {msg.role === 'assistant' && msg.artifact && (
                    <button
                      onClick={() => {
                        setActiveArtifact(msg.artifact);
                        setShowArtifact(true);
                      }}
                      style={{
                        marginTop: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        background: COLORS.surface,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        maxWidth: '80%',
                        textAlign: 'left',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = COLORS.accent;
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = COLORS.border;
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {(() => {
                        const artifactConfig = ARTIFACT_TYPES[msg.artifact.type] || ARTIFACT_TYPES.document;
                        const Icon = artifactConfig.icon;
                        return (
                          <>
                            <div 
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '10px',
                                background: `${artifactConfig.color}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Icon size={20} color={artifactConfig.color} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: COLORS.textPrimary }}>
                                {msg.artifact.title}
                              </p>
                              <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.textSecondary }}>
                                {msg.artifact.preview || msg.artifact.subtitle}
                              </p>
                            </div>
                            <ChevronRight size={18} color={COLORS.textMuted} />
                          </>
                        );
                      })()}
                    </button>
                  )}
                  
                  {/* Actions pour les messages assistant */}
                  {msg.role === 'assistant' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '8px' }}>
                      <button
                        onClick={() => handleCopy(msg.content, msg.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '6px 10px',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          color: COLORS.textMuted,
                          fontSize: '12px',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = COLORS.surfaceHover;
                          e.currentTarget.style.color = COLORS.textSecondary;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = COLORS.textMuted;
                        }}
                      >
                        {copiedId === msg.id ? <Check size={14} /> : <Copy size={14} />}
                        <span>{copiedId === msg.id ? 'Copié !' : 'Copier'}</span>
                      </button>
                      
                      {msg.credits_consumed > 0 && (
                        <span style={{ fontSize: '11px', color: COLORS.textMuted, marginLeft: '8px' }}>
                          -{msg.credits_consumed} AbracaCoins
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Indicateur de chargement */}
              {isLoading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div 
                    style={{
                      padding: '14px 18px',
                      borderRadius: '18px 18px 18px 4px',
                      background: COLORS.surfaceHover,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Loader2 size={16} color={COLORS.textMuted} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
                      <span style={{ fontSize: '14px', color: COLORS.textSecondary }}>Abra réfléchit...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Zone de saisie */}
        <div 
          style={{
            padding: '16px 24px 24px',
            borderTop: `1px solid ${COLORS.border}`,
            background: COLORS.surface,
          }}
        >
          <div 
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '12px',
              padding: '12px 16px',
              background: COLORS.surfaceHover,
              borderRadius: '16px',
              border: `1px solid ${COLORS.border}`,
            }}
          >
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Demande à Abra..."
              rows={1}
              style={{
                flex: 1,
                resize: 'none',
                border: 'none',
                background: 'transparent',
                fontSize: '15px',
                color: COLORS.textPrimary,
                outline: 'none',
                minHeight: '24px',
                maxHeight: '120px',
                lineHeight: '1.5',
              }}
            />
            
            {/* Bouton Micro */}
            <button
              onClick={() => setIsRecording(!isRecording)}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                border: 'none',
                background: isRecording ? COLORS.error : 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}
            >
              {isRecording ? (
                <StopCircle size={20} color="#ffffff" />
              ) : (
                <Mic size={20} color={COLORS.textMuted} />
              )}
            </button>
            
            {/* Bouton Envoyer */}
            <button
              onClick={() => handleSendMessage()}
              disabled={!inputValue.trim() || isLoading}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                border: 'none',
                background: inputValue.trim() ? COLORS.accent : COLORS.border,
                cursor: inputValue.trim() ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}
            >
              <Send size={18} color={inputValue.trim() ? '#ffffff' : COLORS.textMuted} />
            </button>
          </div>
          
          <p style={{ 
            margin: '8px 0 0', 
            fontSize: '11px', 
            color: COLORS.textMuted, 
            textAlign: 'center' 
          }}>
            Abra peut faire des erreurs. Vérifie les informations importantes.
          </p>
        </div>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════════
          COLONNE ARTIFACT VIEWER (Droite)
          ═══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showArtifact && activeArtifact && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: '45%', opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{
              background: COLORS.surface,
              borderLeft: `1px solid ${COLORS.border}`,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header Artifact */}
            <div 
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: `1px solid ${COLORS.border}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {(() => {
                  const artifactConfig = ARTIFACT_TYPES[activeArtifact.type] || ARTIFACT_TYPES.document;
                  const Icon = artifactConfig.icon;
                  return (
                    <>
                      <div 
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          background: `${artifactConfig.color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon size={18} color={artifactConfig.color} />
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: COLORS.textPrimary }}>
                          {activeArtifact.title}
                        </h3>
                        <p style={{ margin: 0, fontSize: '12px', color: COLORS.textSecondary }}>
                          {activeArtifact.subtitle}
                        </p>
                      </div>
                    </>
                  );
                })()}
              </div>
              
              {/* Actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => handleCopy(activeArtifact.content, 'artifact')}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    border: `1px solid ${COLORS.border}`,
                    background: COLORS.surface,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Copier"
                >
                  {copiedId === 'artifact' ? <Check size={16} color={COLORS.success} /> : <Copy size={16} color={COLORS.textSecondary} />}
                </button>
                
                <button
                  onClick={() => handleDownload(activeArtifact)}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    border: `1px solid ${COLORS.border}`,
                    background: COLORS.surface,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Télécharger"
                >
                  <Download size={16} color={COLORS.textSecondary} />
                </button>
                
                <button
                  onClick={() => {
                    setShowArtifact(false);
                    setActiveArtifact(null);
                  }}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    border: `1px solid ${COLORS.border}`,
                    background: COLORS.surface,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Fermer"
                >
                  <X size={16} color={COLORS.textSecondary} />
                </button>
              </div>
            </div>
            
            {/* Contenu Artifact */}
            <div 
              style={{
                flex: 1,
                padding: '20px',
                overflowY: 'auto',
                background: '#fafafa',
              }}
            >
              <pre 
                style={{
                  margin: 0,
                  padding: '20px',
                  background: COLORS.surface,
                  borderRadius: '12px',
                  border: `1px solid ${COLORS.border}`,
                  fontSize: '13px',
                  lineHeight: '1.6',
                  color: COLORS.textPrimary,
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                }}
              >
                {activeArtifact.content}
              </pre>
            </div>
            
            {/* Footer Artifact avec actions */}
            <div 
              style={{
                padding: '16px 20px',
                borderTop: `1px solid ${COLORS.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <button
                onClick={() => handleDownload(activeArtifact)}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  background: COLORS.accent,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = COLORS.accentHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = COLORS.accent;
                }}
              >
                <Download size={18} />
                Télécharger
              </button>
              
              <button
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  background: COLORS.surface,
                  color: COLORS.textPrimary,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                <Share2 size={18} />
                Partager
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Bouton pour réouvrir l'artifact (si fermé) */}
      {!showArtifact && messages.some(m => m.artifact) && (
        <button
          onClick={() => {
            const lastArtifact = [...messages].reverse().find(m => m.artifact)?.artifact;
            if (lastArtifact) {
              setActiveArtifact(lastArtifact);
              setShowArtifact(true);
            }
          }}
          style={{
            position: 'absolute',
            right: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            border: `1px solid ${COLORS.border}`,
            background: COLORS.surface,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
          title="Ouvrir le document"
        >
          <PanelRightOpen size={18} color={COLORS.textSecondary} />
        </button>
      )}
      
      {/* Style pour l'animation de rotation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// GÉNÉRATEURS DE CONTENU DÉMO
// ══════════════════════════════════════════════════════════════════════════════

function generateDevisContent() {
  const num = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  const date = new Date().toLocaleDateString('fr-FR');
  return `
═══════════════════════════════════════════════════════════
                         DEVIS N° DEV-2024-${num}
═══════════════════════════════════════════════════════════

Date : ${date}
Validité : 30 jours

CLIENT
──────────────────────────────────────────────────────────
Nom     : M. Dupont
Adresse : 12 rue des Lilas, 75012 Paris
Tél     : 06 12 34 56 78
Email   : jean.dupont@email.fr

DESCRIPTION DES TRAVAUX
──────────────────────────────────────────────────────────

1. Dépose baignoire existante
   Quantité : 1 forfait
   Prix unitaire : 150,00 €
   Total : 150,00 €

2. Fourniture et pose douche italienne
   Receveur 120x80, paroi verre 8mm
   Quantité : 1 forfait
   Prix unitaire : 2 800,00 €
   Total : 2 800,00 €

3. Carrelage sol (fourniture + pose)
   Grès cérame 60x60 antidérapant
   Quantité : 8 m²
   Prix unitaire : 85,00 €
   Total : 680,00 €

4. Carrelage mural (fourniture + pose)
   Faïence 30x60 blanc mat
   Quantité : 12 m²
   Prix unitaire : 75,00 €
   Total : 900,00 €

5. Plomberie (raccordements)
   Évacuation + alimentation
   Quantité : 1 forfait
   Prix unitaire : 450,00 €
   Total : 450,00 €

──────────────────────────────────────────────────────────
RÉCAPITULATIF
──────────────────────────────────────────────────────────

Total HT  : 4 980,00 €
TVA 20%   :   996,00 €
═══════════════════════════════════════════════════════════
TOTAL TTC : 5 976,00 €
═══════════════════════════════════════════════════════════

CONDITIONS
──────────────────────────────────────────────────────────
• Acompte de 30% à la commande
• Solde à la fin des travaux
• Garantie décennale
• Délai d'intervention : 2 semaines après acceptation

Signature client :                    Signature entreprise :


_____________________                 _____________________
`.trim();
}

function generateFactureContent() {
  const num = Math.floor(Math.random() * 100).toString().padStart(3, '0');
  const date = new Date().toLocaleDateString('fr-FR');
  return `
═══════════════════════════════════════════════════════════
                      FACTURE N° F-2024-${num}
═══════════════════════════════════════════════════════════

Date d'émission : ${date}
Date d'échéance : ${new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR')}

CLIENT
──────────────────────────────────────────────────────────
Nom     : M. Dupont
Adresse : 12 rue des Lilas, 75012 Paris

PRESTATIONS RÉALISÉES
──────────────────────────────────────────────────────────

Rénovation complète salle de bain
Conformément au devis DEV-2024-XXX accepté le XX/XX/2024

──────────────────────────────────────────────────────────

Total HT  : 4 980,00 €
TVA 20%   :   996,00 €
═══════════════════════════════════════════════════════════
TOTAL TTC : 5 976,00 €
═══════════════════════════════════════════════════════════

MODE DE PAIEMENT
──────────────────────────────────────────────────────────
Virement bancaire :
IBAN : FR76 1234 5678 9012 3456 7890 123
BIC  : ABCDEFGH

Règlement à 30 jours.
`.trim();
}

function generateEmailContent() {
  return `
Objet : Votre projet de rénovation - Suivi

Bonjour,

Je me permets de revenir vers vous concernant votre projet de rénovation de salle de bain dont nous avons discuté.

Suite à notre visite technique, je vous ai transmis un devis détaillé. Je reste à votre entière disposition pour répondre à vos questions ou adapter la proposition selon vos besoins.

N'hésitez pas à me contacter par téléphone ou email si vous souhaitez planifier une nouvelle rencontre.

Dans l'attente de votre retour, je vous souhaite une excellente journée.

Cordialement,

[Votre nom]
[Votre entreprise]
[Téléphone]
[Email]
`.trim();
}

function generatePlanningContent() {
  return `
═══════════════════════════════════════════════════════════
              PLANNING CHANTIER - Rénovation SDB
═══════════════════════════════════════════════════════════

Durée totale estimée : 2 semaines

SEMAINE 1
──────────────────────────────────────────────────────────

Lundi
• Préparation chantier
• Protection des sols et murs adjacents
• Installation matériel

Mardi - Mercredi
• Démolition sanitaires existants
• Dépose carrelage
• Évacuation gravats

Jeudi - Vendredi
• Travaux de plomberie
• Modification réseau d'eau
• Test étanchéité

SEMAINE 2
──────────────────────────────────────────────────────────

Lundi - Mardi
• Pose receveur douche
• Installation paroi vitrée
• Raccordements

Mercredi - Jeudi
• Pose carrelage sol
• Pose carrelage mural
• Joints

Vendredi
• Finitions
• Nettoyage chantier
• Réception travaux

══════════════════════════════════════════════════════════
INTERVENANTS
──────────────────────────────────────────────────────────
• Plombier : Pierre D.
• Carreleur : Marc L.
• Chef d'équipe : Jean M.

CONTACT CHANTIER : 06 XX XX XX XX
═══════════════════════════════════════════════════════════
`.trim();
}
